import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Lock,
  Eye,
  EyeOff,
  Mail,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { logDebug, logInfo, logError } from "@/utils/logger";

type FlowType = "forced-first-login" | "email-recovery";

const getRecoveryContext = () => {
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);
  const searchParams = new URLSearchParams(window.location.search);

  return {
    type: searchParams.get("type") || hashParams.get("type"),
    hasTokens:
      !!(searchParams.get("access_token") || hashParams.get("access_token")) &&
      !!(searchParams.get("refresh_token") || hashParams.get("refresh_token")),
  };
};

const resolveFlowType = (
  session: { user?: { user_metadata?: Record<string, any> } } | null,
  authEvent?: string
): FlowType => {
  if (
    session?.user?.user_metadata?.needs_password_change ||
    sessionStorage.getItem("force_password_change") === "true"
  ) {
    return "forced-first-login";
  }

  const recovery = getRecoveryContext();
  if (
    authEvent === "PASSWORD_RECOVERY" ||
    recovery.type === "recovery" ||
    recovery.hasTokens
  ) {
    return "email-recovery";
  }

  return "email-recovery";
};

interface PasswordRequirement {
  text: string;
  met: boolean;
}

const getPasswordRequirements = (password: string): PasswordRequirement[] => [
  { text: "Pelo menos 8 caracteres", met: password.length >= 8 },
  { text: "Uma letra maiúscula", met: /[A-Z]/.test(password) },
  { text: "Uma letra minúscula", met: /[a-z]/.test(password) },
  { text: "Um número", met: /\d/.test(password) },
  {
    text: "Um caractere especial",
    met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  },
];

const getPasswordStrength = (
  reqs: PasswordRequirement[]
): { level: string; color: string } => {
  const met = reqs.filter((r) => r.met).length;
  if (met <= 1) return { level: "Muito fraca", color: "text-red-600" };
  if (met <= 2) return { level: "Fraca", color: "text-orange-600" };
  if (met <= 3) return { level: "Média", color: "text-yellow-600" };
  if (met <= 4) return { level: "Forte", color: "text-blue-600" };
  return { level: "Muito forte", color: "text-green-600" };
};

const NovaSenhaPage = () => {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);

  // Auth state for the page
  const [isReady, setIsReady] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [flowType, setFlowType] = useState<FlowType>("email-recovery");

  // Resend block (when link is invalid/expired)
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  // Avoid concurrent inits
  const initRef = useRef(false);

  const passwordRequirements = useMemo(
    () => getPasswordRequirements(password),
    [password]
  );
  const allRequirementsMet = passwordRequirements.every((r) => r.met);
  const passwordsMatch =
    password.length > 0 && password === confirmPassword;
  const canSubmit = allRequirementsMet && passwordsMatch;
  const strengthInfo = getPasswordStrength(passwordRequirements);

  /**
   * Initialize the page:
   *  1) If sessionStorage flag "force_password_change" is set => forced-first-login
   *     We just check that there is an authenticated session.
   *  2) Otherwise we treat it as email-recovery:
   *     - Listen to onAuthStateChange (PASSWORD_RECOVERY / SIGNED_IN)
   *     - Also call getSession() in case the URL hash was already processed
   *     - Both branches mark the page ready and enable the form
   */
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    let mounted = true;
    let timeoutId: number | null = null;
    let unsubscribe: (() => void) | null = null;

    const setReady = (
      session: { user: { id: string; email?: string | null } } | null,
      flow: FlowType
    ) => {
      if (!mounted) return;
      setFlowType(flow);
      if (session?.user) {
        setUserEmail(session.user.email ?? "");
        setHasValidSession(true);
      } else {
        setHasValidSession(false);
      }
      setIsReady(true);
    };

    const init = async () => {
      try {
        const { data: sub } = supabase.auth.onAuthStateChange(
          (event, session) => {
            logDebug("nova-senha: auth event", {
              component: "NovaSenhaPage",
              metadata: {
                event,
                hasUser: !!session?.user,
                needsPasswordChange:
                  !!session?.user?.user_metadata?.needs_password_change,
              },
            });
            if (session?.user) {
              if (timeoutId) {
                window.clearTimeout(timeoutId);
                timeoutId = null;
              }
              setReady(session, resolveFlowType(session, event));
            }
          }
        );
        unsubscribe = () => sub.subscription.unsubscribe();

        const { data } = await supabase.auth.getSession();
        if (data.session?.user) {
          if (timeoutId) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
          }
          setReady(data.session, resolveFlowType(data.session));
        } else {
          timeoutId = window.setTimeout(() => {
            if (!mounted) return;
            logInfo("nova-senha: recovery timeout, no session found", {
              component: "NovaSenhaPage",
            });
            setReady(null, resolveFlowType(null));
          }, 6000);
        }
      } catch (err) {
        logError(
          "nova-senha: init exception",
          err as Error,
          { component: "NovaSenhaPage" }
        );
        setReady(null, "email-recovery");
      }
    };

    init();

    return () => {
      mounted = false;
      if (timeoutId) window.clearTimeout(timeoutId);
      unsubscribe?.();
    };
  }, []);

  const handleSavePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      toast.error("Complete todos os requisitos da senha");
      return;
    }

    setLoading(true);
    logInfo("nova-senha: updating password", {
      component: "NovaSenhaPage",
      metadata: { flowType },
    });

    try {
      let currentSession = (await supabase.auth.getSession()).data.session;

      if (!currentSession?.user) {
        const { data: refreshed, error: refreshError } =
          await supabase.auth.refreshSession();

        if (refreshError) {
          logError("nova-senha: refresh before update failed", refreshError, {
            component: "NovaSenhaPage",
          });
        }

        currentSession = refreshed.session;
      }

      if (!currentSession?.user) {
        setLoading(false);
        toast.error("Sessão expirada", {
          description: "Faça login novamente para definir sua nova senha.",
        });
        return;
      }

      const effectiveFlowType = resolveFlowType(currentSession);
      setHasValidSession(true);
      setUserEmail(currentSession.user.email ?? "");
      setFlowType(effectiveFlowType);

      // Single source of truth: use the supabase client with the active session.
      // Works for both:
      //  - forced-first-login (regular session with needs_password_change)
      //  - email-recovery (recovery session)
      const { error } = await supabase.auth.updateUser({
        password,
        data: { needs_password_change: false },
      });

      if (error) {
        logError("nova-senha: updateUser failed", error, {
          component: "NovaSenhaPage",
        });

        const lower = (error.message || "").toLowerCase();
        if (lower.includes("same") || lower.includes("different")) {
          toast.error("A nova senha precisa ser diferente da atual.");
        } else if (lower.includes("weak") || lower.includes("password")) {
          toast.error("Senha não atende aos requisitos de segurança.");
        } else {
          toast.error("Erro ao salvar nova senha", {
            description: error.message || "Tente novamente.",
          });
        }
        setLoading(false);
        return;
      }

      logInfo("nova-senha: password updated", {
        component: "NovaSenhaPage",
        metadata: { flowType },
      });

      const wasForceChange = effectiveFlowType === "forced-first-login";

      toast.success("Senha redefinida com sucesso!", {
        description: wasForceChange
          ? "Redirecionando para o painel..."
          : "Faça login com sua nova senha.",
      });

      // Clear protection flags
      sessionStorage.removeItem("force_password_change");
      sessionStorage.removeItem("password_reset_in_progress");

      if (wasForceChange) {
        // Refresh so AuthContext sees needs_password_change cleared, then go to panel
        try {
          await supabase.auth.refreshSession();
        } catch (e) {
          logError("nova-senha: refreshSession failed", e as Error, {
            component: "NovaSenhaPage",
          });
        }
        setTimeout(() => {
          window.location.href = "/fornecedor/painel/inicio";
        }, 800);
      } else {
        // Recovery flow: sign out so the user authenticates with new password
        await supabase.auth.signOut();
        setTimeout(() => navigate("/login"), 800);
      }
    } catch (err: any) {
      logError("nova-senha: updateUser exception", err, {
        component: "NovaSenhaPage",
      });
      toast.error("Erro inesperado", {
        description: err?.message || "Tente novamente mais tarde.",
      });
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!resendEmail || !/\S+@\S+\.\S+/.test(resendEmail)) {
      toast.error("Digite um email válido");
      return;
    }
    setResendLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        resendEmail,
        { redirectTo: `${window.location.origin}/nova-senha` }
      );
      if (error) {
        toast.error("Erro ao enviar email", { description: error.message });
      } else {
        toast.success("Email enviado!", {
          description:
            "Verifique sua caixa de entrada e clique no novo link.",
        });
      }
    } catch (err: any) {
      toast.error("Erro inesperado", {
        description: err?.message || "Tente novamente mais tarde.",
      });
    } finally {
      setResendLoading(false);
    }
  };

  // Loading state
  if (!isReady) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow py-16 bg-gradient-to-b from-accent to-white">
          <div className="container-custom">
            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">
                  Validando link...
                </span>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Invalid session screen (only for email-recovery without session)
  if (!hasValidSession && flowType === "email-recovery") {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow py-16 bg-gradient-to-b from-accent to-white">
          <div className="container-custom">
            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8">
              <div className="text-center space-y-6">
                <div className="flex justify-center">
                  <XCircle className="h-16 w-16 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-foreground">
                  Link inválido ou expirado
                </h1>
                <p className="text-muted-foreground">
                  O link de redefinição de senha não é mais válido. Solicite
                  um novo link abaixo.
                </p>
                <div className="space-y-4 text-left">
                  <div className="space-y-2">
                    <Label
                      htmlFor="resendEmail"
                      className="text-foreground font-medium"
                    >
                      Digite seu email
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="resendEmail"
                        type="email"
                        placeholder="seu-email@exemplo.com"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleResendEmail}
                    disabled={resendLoading}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {resendLoading ? "Enviando..." : "Reenviar email"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/esqueci-senha")}
                    className="w-full"
                  >
                    Voltar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Forced flow with no session: send back to login
  if (!hasValidSession && flowType === "forced-first-login") {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow py-16 bg-gradient-to-b from-accent to-white">
          <div className="container-custom">
            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 text-center space-y-6">
              <h1 className="text-2xl font-bold text-foreground">
                Sessão não encontrada
              </h1>
              <p className="text-muted-foreground">
                Faça login com sua senha temporária para criar sua nova senha.
              </p>
              <Button
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => {
                  sessionStorage.removeItem("force_password_change");
                  sessionStorage.removeItem("password_reset_in_progress");
                  navigate("/login");
                }}
              >
                Ir para o login
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Form
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow py-16 bg-gradient-to-b from-accent to-white">
        <div className="container-custom">
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8">
            <h1 className="text-2xl font-bold text-center text-foreground mb-6">
              {flowType === "forced-first-login"
                ? "Defina sua nova senha"
                : "Crie sua nova senha"}
            </h1>

            {userEmail && (
              <div className="mb-6 p-3 bg-accent rounded-lg flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {userEmail}
                </span>
              </div>
            )}

            <form onSubmit={handleSavePassword} className="space-y-6">
              <div className="space-y-2">
                <Label
                  htmlFor="password"
                  className="text-foreground font-medium"
                >
                  Nova senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPasswords ? "text" : "password"}
                    placeholder="Digite sua nova senha"
                    required
                    className="pl-10 pr-10 focus:border-primary transition-colors"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswords ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {password && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        Força da senha:
                      </span>
                      <span
                        className={`text-sm font-medium ${strengthInfo.color}`}
                      >
                        {strengthInfo.level}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          strengthInfo.level === "Muito fraca"
                            ? "bg-red-500 w-1/5"
                            : strengthInfo.level === "Fraca"
                            ? "bg-orange-500 w-2/5"
                            : strengthInfo.level === "Média"
                            ? "bg-yellow-500 w-3/5"
                            : strengthInfo.level === "Forte"
                            ? "bg-blue-500 w-4/5"
                            : "bg-green-500 w-full"
                        }`}
                      />
                    </div>
                  </div>
                )}
              </div>

              {password && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Requisitos da senha:
                  </Label>
                  <div className="space-y-1">
                    {passwordRequirements.map((req, index) => (
                      <div key={index} className="flex items-center gap-2">
                        {req.met ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-400" />
                        )}
                        <span
                          className={`text-sm ${
                            req.met ? "text-green-600" : "text-red-400"
                          }`}
                        >
                          {req.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label
                  htmlFor="confirmPassword"
                  className="text-foreground font-medium"
                >
                  Confirmar nova senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPasswords ? "text" : "password"}
                    placeholder="Digite novamente sua senha"
                    required
                    className="pl-10 pr-10 focus:border-primary transition-colors"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswords ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {confirmPassword && (
                  <div className="flex items-center gap-2">
                    {passwordsMatch ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordsMatch ? "text-green-600" : "text-red-400"
                      }`}
                    >
                      {passwordsMatch
                        ? "As senhas coincidem"
                        : "As senhas não coincidem"}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="showPassword"
                  checked={showPasswords}
                  onCheckedChange={(checked) =>
                    setShowPasswords(checked as boolean)
                  }
                />
                <Label
                  htmlFor="showPassword"
                  className="text-sm text-muted-foreground"
                >
                  Mostrar senhas
                </Label>
              </div>

              <Button
                type="submit"
                disabled={loading || !canSubmit}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Salvando...
                  </span>
                ) : (
                  "Salvar nova senha"
                )}
              </Button>

              {flowType === "email-recovery" && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/login")}
                  className="w-full"
                  disabled={loading}
                >
                  Voltar
                </Button>
              )}
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NovaSenhaPage;
